import { useCallback, useEffect, useRef, useState } from "react";
import {
  parseAiEvent,
  parseChangeSetStatusEvent,
  parseCommandStatusEvent,
  type CommandView,
  type ChangeSetInfo,
  type AiEvent,
  type ChatSession,
  type ExecutionEnvironment,
  type LastModel,
  type ProviderInfo,
  type ContextItem,
} from "@racler/protocol";
import { api } from "./api.ts";
import {
  applyAiEvent,
  emptyChat,
  upsertChangeSet,
  upsertCommand,
  withSentAndBuffered,
  type ChatState,
} from "./chatReducer.ts";
import { useRelay } from "./relayContext.tsx";

export type { Selection } from "./composer.ts";
import type { Selection } from "./composer.ts";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * Chat state for one (machine, Project). Streaming events update the open chat; everything else is read from
 * the server, so a reload shows exactly what was persisted (plus the live partial answer of a running response).
 */
export function useChat(machineId: string, projectId: string, initialChatId?: string) {
  const relay = useRelay();
  const [chat, setChat] = useState<ChatState>(emptyChat);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [providers, setProviders] = useState<ProviderInfo[] | null>(null);
  const [lastModel, setLastModel] = useState<LastModel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastPrompt, setLastPrompt] = useState<string | null>(null);
  const [activeEnv, setActiveEnv] = useState<ExecutionEnvironment | null>(null);
  const sendingRef = useRef(false);
  const bufferRef = useRef<AiEvent[]>([]);
  const sessionRef = useRef<string | null>(null);
  const initialChat = useRef(initialChatId);
  sessionRef.current = chat.sessionId;

  const refreshSessions = useCallback(async () => {
    const r = await relay.chat(machineId, "agent/chat.sessions", { projectId });
    setSessions(r.sessions);
    setLastModel(r.lastModel);
    return r;
  }, [relay, machineId, projectId]);

  const refreshEnv = useCallback(async () => {
    if (!machineId || !projectId) return;
    try {
      const res = await api.activeEnvironment(machineId, projectId);
      setActiveEnv(res.environment);
    } catch {
      setActiveEnv(null);
    }
  }, [machineId, projectId]);

  const loadModels = useCallback(async () => {
    try {
      setProviders((await relay.machine(machineId, "agent/ai.models", {})).providers);
    } catch (e) {
      setProviders([]);
      setError(`Could not load models: ${errText(e)}`);
    }
  }, [relay, machineId]);

  const open = useCallback(
    async (sessionId: string) => {
      try {
        const r = await relay.chat(machineId, "agent/chat.session", { sessionId });
        const activeRun = r.activeRun ?? null;
        const isRunning = r.activeRunId !== null || activeRun?.status === "running";
        setChat((prev) => {
          if (prev.sessionId !== sessionId) {
            return {
              sessionId,
              messages: r.messages,
              activeRunId: isRunning ? (r.activeRunId ?? activeRun?.id ?? null) : null,
              activeRun,
              changeSets: r.changeSets,
              commands: r.commands,
            };
          }
          // Merge server messages with live local state:
          // A live continuation run may have already streamed deltas or completed via WebSocket
          // before chat.session finished querying the DB. Preserve the live streamed content and status.
          const merged = r.messages.map((serverMsg) => {
            const liveMsg = prev.messages.find(
              (m) => m.id === serverMsg.id || (m.runId && serverMsg.runId && m.runId === serverMsg.runId),
            );
            if (!liveMsg) return serverMsg;
            const content = liveMsg.content.length > serverMsg.content.length ? liveMsg.content : serverMsg.content;
            const status =
              (liveMsg.status === "complete" || liveMsg.status === "error") && serverMsg.status === "running"
                ? liveMsg.status
                : serverMsg.status;
            const activity =
              liveMsg.activity.length > serverMsg.activity.length ? liveMsg.activity : serverMsg.activity;
            return {
              ...serverMsg,
              content,
              status,
              activity,
              stopReason: liveMsg.stopReason ?? serverMsg.stopReason,
            };
          });
          // Also keep any synthetic messages that are currently streaming and not yet in r.messages
          for (const liveMsg of prev.messages) {
            if (!merged.some((m) => m.id === liveMsg.id || (m.runId && liveMsg.runId && m.runId === liveMsg.runId))) {
              merged.push(liveMsg);
            }
          }
          return {
            sessionId,
            messages: merged,
            activeRunId: isRunning ? (r.activeRunId ?? activeRun?.id ?? null) : null,
            activeRun,
            changeSets: r.changeSets,
            commands: r.commands,
          };
        });
        setError(null);
      } catch (e) {
        setError(errText(e));
      }
    },
    [relay, machineId],
  );

  const newChat = useCallback(() => {
    setChat(emptyChat);
    setError(null);
    setLastPrompt(null);
  }, []);

  // Initial load, and again after the socket reconnects (events during the gap were persisted by the server).
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        await relay.server("system/machine.subscribe", { machineId });
        const r = await refreshSessions();
        await refreshEnv();
        if (cancelled) return;
        const wanted = initialChat.current;
        initialChat.current = undefined;
        if (sessionRef.current) await open(sessionRef.current);
        else if (wanted && r.sessions.some((x) => x.id === wanted)) await open(wanted);
        else if (r.sessions[0]) await open(r.sessions[0].id);
      } catch (e) {
        if (!cancelled) setError(errText(e));
      }
    };
    void loadModels();
    if (relay.connectionState === "open") void init();
    const off = relay.onReconnect(() => void init());
    return () => {
      cancelled = true;
      off();
    };
  }, [relay, machineId, refreshSessions, open, loadModels, refreshEnv]);

  useEffect(
    () =>
      relay.on((msg) => {
        if (msg.machineId !== machineId) return;
        const cmd = parseCommandStatusEvent(msg);
        if (cmd) {
          setChat((s) => upsertCommand(s, cmd));
          return;
        }
        const cs = parseChangeSetStatusEvent(msg);
        if (cs) {
          setChat((s) => upsertChangeSet(s, cs));
          void refreshEnv();
          return;
        }
        const ev = parseAiEvent(msg);
        if (!ev) return;
        if (sendingRef.current) {
          // The run may be streaming before chat.send resolved: keep the events and replay them afterwards.
          if (bufferRef.current.length < 5000) bufferRef.current.push(ev);
          return;
        }
        setChat((s) => applyAiEvent(s, ev));
        if (ev.type === "agent/response.completed" || ev.type === "agent/response.error") {
          void refreshSessions();
          void refreshEnv();
          // The server persists the environment's final state just AFTER forwarding the completion: re-read it so the
          // UI always converges on the persisted state (never a stale local one).
          for (const ms of [400, 1500]) setTimeout(() => void refreshEnv(), ms);
        }
      }),
    [relay, machineId, refreshSessions, refreshEnv],
  );

  const send = useCallback(
    async (
      content: string,
      sel: Selection,
      contextItems?: ContextItem[],
      environmentType?: "host" | "worktree",
      environmentId?: string,
    ) => {
      setError(null);
      setLastPrompt(content);
      sendingRef.current = true;
      bufferRef.current = [];
      try {
        const sent = await relay.chat(machineId, "agent/chat.send", {
          projectId,
          content,
          provider: sel.provider,
          model: sel.model,
          ...(sessionRef.current ? { sessionId: sessionRef.current } : {}),
          ...(sel.effort ? { effort: sel.effort } : {}),
          mode: sel.mode ?? "chat",
          ...(sel.behavior ? { behavior: sel.behavior } : {}),
          ...(sel.strictReview !== undefined ? { strictReview: sel.strictReview } : {}),
          ...(sel.dod && sel.dod.length > 0 ? { dod: sel.dod } : {}),
          ...(sel.ceilings ? { ceilings: sel.ceilings } : {}),
          ...(contextItems && contextItems.length > 0 ? { contextItems } : {}),
          ...(environmentType ? { environmentType, environmentId } : {}),
        });
        const buffered = bufferRef.current;
        bufferRef.current = [];
        sendingRef.current = false;
        setChat((s) => withSentAndBuffered(s, sent, buffered));
        void refreshSessions();
        void refreshEnv();
      } catch (e) {
        setError(errText(e));
      } finally {
        sendingRef.current = false;
        bufferRef.current = [];
      }
    },
    [relay, machineId, projectId, refreshSessions, refreshEnv],
  );

  /** Hard-deletes a chat on the server (owner only). If it is the open chat, the view returns to a new chat. */
  const remove = useCallback(
    async (sessionId: string) => {
      const r = await relay.chat(machineId, "agent/chat.delete", { sessionId });
      if (sessionRef.current === sessionId) {
        setChat(emptyChat);
        setLastPrompt(null);
      }
      setSessions((list) => list.filter((x) => x.id !== sessionId));
      void refreshSessions();
      return r.deleted;
    },
    [relay, machineId, refreshSessions],
  );

  /** Approve/reject go through the server; the reply (and a status event) carry the new state. */
  const decide = useCallback(
    async (kind: "approve" | "reject", cs: ChangeSetInfo): Promise<ChangeSetInfo> => {
      const r =
        kind === "approve"
          ? await relay.chat(machineId, "agent/changeset.approve", {
              changeSetId: cs.id,
              contentHash: cs.contentHash ?? "",
            })
          : await relay.chat(machineId, "agent/changeset.reject", { changeSetId: cs.id });
      setChat((s) => upsertChangeSet(s, r.changeSet));
      // In Agent mode the decision continues the run: reload to pick up the new assistant message.
      if (sessionRef.current) await open(sessionRef.current);
      if (kind === "approve" && r.changeSet.status !== "applied") {
        throw new Error(
          r.changeSet.message || `Apply ${r.changeSet.status}: the project changed since the proposal`,
        );
      }
      if (kind === "reject" && r.changeSet.status !== "rejected") {
        throw new Error(
          r.changeSet.message || `Reject ${r.changeSet.status}: could not reject changes`,
        );
      }
      return r.changeSet;
    },
    [relay, machineId, open],
  );

  /** Approve / deny a frozen command; an Agent-mode chat then continues as a new run (picked up via reload). */
  const decideCommand = useCallback(
    async (kind: "approve" | "deny", c: CommandView): Promise<void> => {
      const r =
        kind === "approve"
          ? await relay.chat(machineId, "agent/command.approve", {
              commandId: c.id,
              contentHash: c.contentHash,
            })
          : await relay.chat(machineId, "agent/command.deny", { commandId: c.id });
      setChat((s) => upsertCommand(s, r.command));
      if (sessionRef.current) await open(sessionRef.current);
    },
    [relay, machineId, open],
  );

  const cancelCommand = useCallback(
    async (c: CommandView) => {
      await relay.chat(machineId, "agent/command.cancel", { commandId: c.id });
    },
    [relay, machineId],
  );

  const stop = useCallback(async () => {
    const runId = chat.activeRunId ?? (chat.activeRun?.status === "running" ? chat.activeRun.id : null);
    if (!runId) return;
    try {
      await relay.chat(machineId, "agent/chat.cancel", { runId });
    } catch (e) {
      setError(errText(e));
    }
  }, [relay, machineId, chat.activeRunId, chat.activeRun]);

  return {
    chat,
    sessions,
    providers,
    lastModel,
    error,
    lastPrompt,
    send,
    stop,
    open,
    newChat,
    loadModels,
    remove,
    decide,
    decideCommand,
    cancelCommand,
    activeEnv,
    refreshEnv,
  };
}

/** The one chat/session state of a Project. Agent and the optional History panel both read from it. */
export type ChatController = ReturnType<typeof useChat>;
